import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client.ts';

interface NinjaGameProps {
  onBackToHub: () => void;
}

// Procedural Web Audio Sound Generator
function playSlashSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(480, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.13);
  } catch {}
}

function playShurikenSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.09);
  } catch {}
}

function playFireNinjutsuSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(150, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.4);
    gain.gain.setValueAtTime(0.7, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.42);
  } catch {}
}

function playCoinPickupSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(987.77, ctx.currentTime);
    osc.frequency.setValueAtTime(1318.51, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.21);
  } catch {}
}

// Map definition
interface GameMap {
  id: string;
  name: string;
  desc: string;
  bgColor: string;
  groundColor: string;
  monsterName: string;
  monsterIcon: string;
  monsterHp: number;
  monsterAtk: number;
  monsterExp: number;
  bossName: string;
  bossIcon: string;
  bossHp: number;
  bossAtk: number;
  bossExp: number;
}

const MAPS: GameMap[] = [
  {
    id: 'bamboo',
    name: '1. Rừng Trúc Xanh',
    desc: 'Khu rừng rậm ngoại ô làng ninja, nơi ẩn nấp của dơi quỷ và ma cóc.',
    bgColor: '#062016',
    groundColor: '#14532d',
    monsterName: 'Dơi Rừng',
    monsterIcon: '🦇',
    monsterHp: 60,
    monsterAtk: 8,
    monsterExp: 25,
    bossName: 'Đại Ma Cáp',
    bossIcon: '🐸',
    bossHp: 320,
    bossAtk: 18,
    bossExp: 150,
  },
  {
    id: 'village_ruins',
    name: '2. Thung Lũng Cổ',
    desc: 'Di tích cổ ngập tràn ninja hắc ám và linh hồn samurai tha hóa.',
    bgColor: '#1c1917',
    groundColor: '#44403c',
    monsterName: 'Ninja Hắc Ám',
    monsterIcon: '🥷',
    monsterHp: 120,
    monsterAtk: 16,
    monsterExp: 55,
    bossName: 'Tướng Quỷ Samurai',
    bossIcon: '👹',
    bossHp: 650,
    bossAtk: 32,
    bossExp: 350,
  },
  {
    id: 'demon_cave',
    name: '3. Hang Quỷ Đỏ',
    desc: 'Hầm dung nham sâu thẳm dưới lòng đất, ngự trị bởi Chúa Tể Ma Đạo.',
    bgColor: '#2a0808',
    groundColor: '#7f1d1d',
    monsterName: 'Hỏa Xà Quỷ',
    monsterIcon: '🐍',
    monsterHp: 220,
    monsterAtk: 26,
    monsterExp: 110,
    bossName: 'Chúa Tể Ma Đạo',
    bossIcon: '👺',
    bossHp: 1200,
    bossAtk: 50,
    bossExp: 800,
  },
];

interface Monster {
  id: string;
  x: number;
  y: number;
  vx: number;
  hp: number;
  maxHp: number;
  atk: number;
  exp: number;
  icon: string;
  name: string;
  isBoss?: boolean;
  facing: 'left' | 'right';
  patrolMin: number;
  patrolMax: number;
  attackCooldown: number;
}

interface Projectile {
  id: string;
  x: number;
  y: number;
  vx: number;
  type: 'shuriken' | 'fire_wave';
  damage: number;
}

interface DropItem {
  id: string;
  x: number;
  y: number;
  type: 'gold' | 'herb' | 'chakra_pill';
  value: number;
}

interface DamageNumber {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

export const NinjaGame: React.FC<NinjaGameProps> = ({ onBackToHub }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Persistent Player Stats
  const [level, setLevel] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('ninja_level') || '1', 10); } catch { return 1; }
  });
  const [exp, setExp] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('ninja_exp') || '0', 10); } catch { return 0; }
  });
  const [gold, setGold] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('ninja_gold') || '50', 10); } catch { return 50; }
  });
  const [shurikens, setShurikens] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('ninja_shurikens') || '30', 10); } catch { return 30; }
  });
  const [swordTier, setSwordTier] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('ninja_sword') || '1', 10); } catch { return 1; }
  });

  // Current Map & Mode
  const [currentMapIndex, setCurrentMapIndex] = useState<number>(0);
  const [inVillage, setInVillage] = useState<boolean>(false);
  const [skillCooldown, setSkillCooldown] = useState<number>(0);

  // Live Gameplay Variables in Refs for 60FPS Canvas Loop
  const currentMap = MAPS[currentMapIndex];
  const maxExp = level * 100;
  const maxHp = 100 + level * 25 + swordTier * 20;
  const maxMp = 80 + level * 15;

  const playerRef = useRef({
    x: 80,
    y: 220,
    vx: 0,
    vy: 0,
    width: 28,
    height: 40,
    isGrounded: false,
    facing: 'right' as 'left' | 'right',
    hp: maxHp,
    mp: maxMp,
    isSlashing: false,
    slashTimer: 0,
    slashBox: { x: 0, y: 0, width: 44, height: 40 },
    invincibleTimer: 0,
  });

  const monstersRef = useRef<Monster[]>([]);
  const projectilesRef = useRef<Projectile[]>([]);
  const dropsRef = useRef<DropItem[]>([]);
  const damageTextsRef = useRef<DamageNumber[]>([]);
  const keysRef = useRef<{ left: boolean; right: boolean; jump: boolean }>({
    left: false,
    right: false,
    jump: false,
  });

  // UI Reactive States for display
  const [uiHp, setUiHp] = useState<number>(maxHp);
  const [uiMp, setUiMp] = useState<number>(maxMp);

  // Spawn Monsters for Current Map
  const spawnMapMonsters = useCallback(() => {
    const list: Monster[] = [];
    const map = MAPS[currentMapIndex];

    // Regular monsters along the map
    list.push({
      id: `m1_${Date.now()}`,
      x: 280,
      y: 220,
      vx: 1.2,
      hp: map.monsterHp,
      maxHp: map.monsterHp,
      atk: map.monsterAtk,
      exp: map.monsterExp,
      icon: map.monsterIcon,
      name: map.monsterName,
      facing: 'left',
      patrolMin: 200,
      patrolMax: 380,
      attackCooldown: 0,
    });

    list.push({
      id: `m2_${Date.now()}`,
      x: 480,
      y: 220,
      vx: 1.4,
      hp: map.monsterHp,
      maxHp: map.monsterHp,
      atk: map.monsterAtk,
      exp: map.monsterExp,
      icon: map.monsterIcon,
      name: map.monsterName,
      facing: 'right',
      patrolMin: 420,
      patrolMax: 560,
      attackCooldown: 0,
    });

    // Map Boss
    list.push({
      id: `boss_${Date.now()}`,
      x: 680,
      y: 220,
      vx: 1.0,
      hp: map.bossHp,
      maxHp: map.bossHp,
      atk: map.bossAtk,
      exp: map.bossExp,
      icon: map.bossIcon,
      name: map.bossName,
      isBoss: true,
      facing: 'left',
      patrolMin: 600,
      patrolMax: 760,
      attackCooldown: 0,
    });

    monstersRef.current = list;
    projectilesRef.current = [];
    dropsRef.current = [];
  }, [currentMapIndex]);

  useEffect(() => {
    spawnMapMonsters();
    playerRef.current.x = 60;
    playerRef.current.y = 220;
    playerRef.current.hp = maxHp;
    playerRef.current.mp = maxMp;
  }, [currentMapIndex, spawnMapMonsters, maxHp, maxMp]);

  // Handle EXP Gain and Level Up
  const addExp = useCallback((amount: number) => {
    setExp((prevExp) => {
      let currentTotal = prevExp + amount;
      let curLevel = level;
      let targetReq = curLevel * 100;

      while (currentTotal >= targetReq) {
        currentTotal -= targetReq;
        curLevel += 1;
        targetReq = curLevel * 100;
        damageTextsRef.current.push({
          id: `lvl_${Date.now()}`,
          x: playerRef.current.x,
          y: playerRef.current.y - 40,
          text: `🎉 LÊN CẤP ${curLevel}!`,
          color: '#fbbf24',
          life: 60,
        });
      }

      setLevel(curLevel);
      try {
        localStorage.setItem('ninja_level', curLevel.toString());
        localStorage.setItem('ninja_exp', currentTotal.toString());
      } catch {}

      api.saveGameProgress('ninja-legacy', { level: curLevel, exp: currentTotal, gold }, `Ninja Phong Ma Cấp ${curLevel}`).catch(() => {});
      return currentTotal;
    });
  }, [level, gold]);

  // Action: Melee Slash
  const handleMeleeAttack = () => {
    if (inVillage) return;
    const p = playerRef.current;
    p.isSlashing = true;
    p.slashTimer = 10;
    p.slashBox = {
      x: p.facing === 'right' ? p.x + 10 : p.x - 45,
      y: p.y - 10,
      width: 48,
      height: 48,
    };

    playSlashSound();

    // Damage enemies in slash box
    const baseDmg = 25 + level * 8 + swordTier * 15;
    monstersRef.current.forEach((m) => {
      if (
        m.x + 20 >= p.slashBox.x &&
        m.x - 20 <= p.slashBox.x + p.slashBox.width &&
        m.y + 20 >= p.slashBox.y &&
        m.y - 20 <= p.slashBox.y + p.slashBox.height
      ) {
        const isCrit = Math.random() < 0.25;
        const dmg = isCrit ? Math.floor(baseDmg * 1.8) : baseDmg;
        m.hp -= dmg;

        damageTextsRef.current.push({
          id: `dmg_${Date.now()}_${Math.random()}`,
          x: m.x,
          y: m.y - 20,
          text: isCrit ? `CHÍ MẠNG -${dmg}!` : `-${dmg}`,
          color: isCrit ? '#f43f5e' : '#f59e0b',
          life: 30,
        });
      }
    });
  };

  // Action: Throw Shuriken
  const handleThrowShuriken = () => {
    if (inVillage) return;
    if (shurikens <= 0) {
      damageTextsRef.current.push({
        id: `no_shur_${Date.now()}`,
        x: playerRef.current.x,
        y: playerRef.current.y - 25,
        text: 'Hết phi tiêu! Vào Làng mua thêm',
        color: '#f87171',
        life: 40,
      });
      return;
    }

    setShurikens((s) => {
      const next = s - 1;
      try { localStorage.setItem('ninja_shurikens', next.toString()); } catch {}
      return next;
    });

    const p = playerRef.current;
    playShurikenSound();

    projectilesRef.current.push({
      id: `proj_${Date.now()}`,
      x: p.facing === 'right' ? p.x + 20 : p.x - 20,
      y: p.y + 10,
      vx: p.facing === 'right' ? 8 : -8,
      type: 'shuriken',
      damage: 35 + level * 6 + swordTier * 10,
    });
  };

  // Action: Fire Ninjutsu (Ultimate Skill)
  const handleFireSkill = () => {
    if (inVillage) return;
    if (skillCooldown > 0) return;
    if (playerRef.current.mp < 35) {
      damageTextsRef.current.push({
        id: `no_mp_${Date.now()}`,
        x: playerRef.current.x,
        y: playerRef.current.y - 25,
        text: 'Chưa đủ Chakra (cần 35 MP)!',
        color: '#60a5fa',
        life: 40,
      });
      return;
    }

    playerRef.current.mp -= 35;
    setSkillCooldown(5);
    playFireNinjutsuSound();

    // Spawn 2 blazing fire waves both directions
    projectilesRef.current.push({
      id: `skill_r_${Date.now()}`,
      x: playerRef.current.x + 10,
      y: playerRef.current.y + 5,
      vx: 6.5,
      type: 'fire_wave',
      damage: 80 + level * 15 + swordTier * 25,
    });

    projectilesRef.current.push({
      id: `skill_l_${Date.now()}`,
      x: playerRef.current.x - 10,
      y: playerRef.current.y + 5,
      vx: -6.5,
      type: 'fire_wave',
      damage: 80 + level * 15 + swordTier * 25,
    });
  };

  // Skill Cooldown Tick
  useEffect(() => {
    if (skillCooldown > 0) {
      const t = setTimeout(() => setSkillCooldown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [skillCooldown]);

  // Village Shop: Buy Shurikens
  const buyShurikens = () => {
    if (gold < 20) return;
    setGold((g) => {
      const ng = g - 20;
      try { localStorage.setItem('ninja_gold', ng.toString()); } catch {}
      return ng;
    });
    setShurikens((s) => {
      const ns = s + 20;
      try { localStorage.setItem('ninja_shurikens', ns.toString()); } catch {}
      return ns;
    });
    playCoinPickupSound();
  };

  // Village Shop: Upgrade Katana
  const upgradeSword = () => {
    const cost = swordTier * 80;
    if (gold < cost) return;
    setGold((g) => {
      const ng = g - cost;
      try { localStorage.setItem('ninja_gold', ng.toString()); } catch {}
      return ng;
    });
    setSwordTier((t) => {
      const nt = t + 1;
      try { localStorage.setItem('ninja_sword', nt.toString()); } catch {}
      return nt;
    });
    playCoinPickupSound();
  };

  // Village Rest: Restore HP & MP
  const restAtInn = () => {
    playerRef.current.hp = maxHp;
    playerRef.current.mp = maxMp;
    setUiHp(maxHp);
    setUiMp(maxMp);
    playCoinPickupSound();
  };

  // Main 60FPS Game Loop
  useEffect(() => {
    if (inVillage) return;
    let animId: number;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const GROUND_Y = 250;
    const GRAVITY = 0.55;

    const gameLoop = () => {
      const p = playerRef.current;

      // 1. Player Physics & Controls
      if (keysRef.current.left) {
        p.vx = -4.2;
        p.facing = 'left';
      } else if (keysRef.current.right) {
        p.vx = 4.2;
        p.facing = 'right';
      } else {
        p.vx *= 0.75;
      }

      if (keysRef.current.jump && p.isGrounded) {
        p.vy = -10.8;
        p.isGrounded = false;
      }

      p.vy += GRAVITY;
      p.x += p.vx;
      p.y += p.vy;

      // Floor Collision
      if (p.y >= GROUND_Y) {
        p.y = GROUND_Y;
        p.vy = 0;
        p.isGrounded = true;
      }

      // World Boundaries
      if (p.x < 15) p.x = 15;
      if (p.x > 780) p.x = 780;

      // Slash Timer
      if (p.slashTimer > 0) {
        p.slashTimer--;
        if (p.slashTimer === 0) p.isSlashing = false;
      }

      if (p.invincibleTimer > 0) p.invincibleTimer--;

      // MP Regeneration
      if (p.mp < maxMp && Math.random() < 0.05) {
        p.mp = Math.min(maxMp, p.mp + 1);
      }

      // 2. Projectiles Update
      projectilesRef.current = projectilesRef.current.filter((proj) => {
        proj.x += proj.vx;

        // Check monster hits
        for (const m of monstersRef.current) {
          if (Math.abs(proj.x - m.x) < 25 && Math.abs(proj.y - m.y) < 30) {
            m.hp -= proj.damage;
            damageTextsRef.current.push({
              id: `p_dmg_${Date.now()}_${Math.random()}`,
              x: m.x,
              y: m.y - 20,
              text: `-${proj.damage}`,
              color: proj.type === 'fire_wave' ? '#f97316' : '#38bdf8',
              life: 30,
            });
            if (proj.type === 'shuriken') return false; // shuriken consumes on hit
          }
        }

        return proj.x > 0 && proj.x < 800;
      });

      // 3. Monsters Update & Patrol AI
      monstersRef.current.forEach((m) => {
        const distToPlayer = Math.abs(m.x - p.x);

        // Aggro & chase if player is close
        if (distToPlayer < 180) {
          if (m.x > p.x) {
            m.vx = -1.6;
            m.facing = 'left';
          } else {
            m.vx = 1.6;
            m.facing = 'right';
          }
        } else {
          // Normal Patrol
          m.x += m.vx;
          if (m.x >= m.patrolMax) {
            m.vx = -Math.abs(m.vx);
            m.facing = 'left';
          } else if (m.x <= m.patrolMin) {
            m.vx = Math.abs(m.vx);
            m.facing = 'right';
          }
        }

        m.x += m.vx;

        // Attack Player Collision
        if (distToPlayer < 32 && Math.abs(m.y - p.y) < 30 && p.invincibleTimer === 0) {
          p.hp -= m.atk;
          p.invincibleTimer = 35; // invincibility frames
          damageTextsRef.current.push({
            id: `p_hurt_${Date.now()}`,
            x: p.x,
            y: p.y - 20,
            text: `-${m.atk}`,
            color: '#ef4444',
            life: 30,
          });

          // Knockback
          p.vx = m.facing === 'right' ? 5 : -5;
          p.vy = -3;

          // Death check
          if (p.hp <= 0) {
            p.hp = maxHp;
            p.x = 60;
            p.y = GROUND_Y;
            damageTextsRef.current.push({
              id: `p_respawn_${Date.now()}`,
              x: 100,
              y: 180,
              text: '💀 Bị thương nặng! Đã hồi sinh tại đầu màn',
              color: '#f87171',
              life: 60,
            });
          }
        }
      });

      // Monster Death & Drops
      monstersRef.current = monstersRef.current.filter((m) => {
        if (m.hp <= 0) {
          addExp(m.exp);

          // Drop Coins & Items
          const dropGold = m.isBoss ? 80 : 15 + Math.floor(Math.random() * 10);
          dropsRef.current.push({
            id: `drop_g_${Date.now()}`,
            x: m.x,
            y: GROUND_Y + 10,
            type: 'gold',
            value: dropGold,
          });

          if (Math.random() < 0.4 || m.isBoss) {
            dropsRef.current.push({
              id: `drop_h_${Date.now()}`,
              x: m.x + 15,
              y: GROUND_Y + 10,
              type: 'herb',
              value: 40,
            });
          }

          damageTextsRef.current.push({
            id: `m_exp_${Date.now()}`,
            x: m.x,
            y: m.y - 35,
            text: `+${m.exp} EXP`,
            color: '#34d399',
            life: 45,
          });

          return false;
        }
        return true;
      });

      // Respawn if all cleared
      if (monstersRef.current.length === 0) {
        setTimeout(spawnMapMonsters, 3000);
      }

      // 4. Loot Pickup Check
      dropsRef.current = dropsRef.current.filter((item) => {
        if (Math.abs(item.x - p.x) < 30 && Math.abs(item.y - p.y) < 35) {
          if (item.type === 'gold') {
            setGold((g) => {
              const ng = g + item.value;
              try { localStorage.setItem('ninja_gold', ng.toString()); } catch {}
              return ng;
            });
            playCoinPickupSound();
            damageTextsRef.current.push({
              id: `coin_${Date.now()}`,
              x: p.x,
              y: p.y - 25,
              text: `+${item.value} Vàng`,
              color: '#eab308',
              life: 30,
            });
          } else if (item.type === 'herb') {
            p.hp = Math.min(maxHp, p.hp + item.value);
            damageTextsRef.current.push({
              id: `heal_${Date.now()}`,
              x: p.x,
              y: p.y - 25,
              text: `+${item.value} HP`,
              color: '#22c55e',
              life: 30,
            });
          }
          return false;
        }
        return true;
      });

      // 5. Damage Numbers Tick
      damageTextsRef.current.forEach((d) => {
        d.y -= 0.7;
        d.life--;
      });
      damageTextsRef.current = damageTextsRef.current.filter((d) => d.life > 0);

      // Sync React UI bars
      setUiHp(Math.max(0, p.hp));
      setUiMp(Math.max(0, p.mp));

      // -------------------------------------------------------------
      // RENDERING (CANVAS)
      // -------------------------------------------------------------
      const W = canvas.width;
      const H = canvas.height;

      // Camera Offset centered on player
      const camX = Math.max(0, Math.min(800 - W, p.x - W / 2));

      ctx.save();
      ctx.clearRect(0, 0, W, H);

      // Background Sky & Parallax
      ctx.fillStyle = currentMap.bgColor;
      ctx.fillRect(0, 0, W, H);

      // Distant mountain silhouettes
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.moveTo(0, 220);
      ctx.lineTo(80, 160);
      ctx.lineTo(200, 230);
      ctx.lineTo(340, 150);
      ctx.lineTo(480, 240);
      ctx.lineTo(600, 170);
      ctx.lineTo(800, 240);
      ctx.lineTo(800, H);
      ctx.lineTo(0, H);
      ctx.fill();

      // Translate by Camera
      ctx.translate(-camX, 0);

      // Ground Terrain
      ctx.fillStyle = currentMap.groundColor;
      ctx.fillRect(0, GROUND_Y + 30, 800, H - (GROUND_Y + 30));

      // Ground Top Border Line
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y + 30);
      ctx.lineTo(800, GROUND_Y + 30);
      ctx.stroke();

      // Draw Drops
      dropsRef.current.forEach((d) => {
        ctx.font = '18px sans-serif';
        ctx.textAlign = 'center';
        if (d.type === 'gold') ctx.fillText('🪙', d.x, d.y);
        else ctx.fillText('🌿', d.x, d.y);
      });

      // Draw Monsters
      monstersRef.current.forEach((m) => {
        // Monster Sprite
        ctx.font = m.isBoss ? '34px sans-serif' : '26px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(m.icon, m.x, m.y + 24);

        // Name & HP Bar
        const barW = m.isBoss ? 60 : 36;
        const barH = 5;
        const barX = m.x - barW / 2;
        const barY = m.y - 10;

        ctx.fillStyle = '#0f172a';
        ctx.fillRect(barX, barY, barW, barH);

        const hpPct = Math.max(0, m.hp / m.maxHp);
        ctx.fillStyle = m.isBoss ? '#ef4444' : '#f59e0b';
        ctx.fillRect(barX, barY, barW * hpPct, barH);

        ctx.fillStyle = '#f8fafc';
        ctx.font = '10px sans-serif';
        ctx.fillText(m.name, m.x, barY - 3);
      });

      // Draw Projectiles
      projectilesRef.current.forEach((proj) => {
        if (proj.type === 'shuriken') {
          ctx.font = '18px sans-serif';
          ctx.fillText('✴️', proj.x, proj.y);
        } else {
          ctx.font = '24px sans-serif';
          ctx.fillText('🔥', proj.x, proj.y);
        }
      });

      // Draw Player Ninja
      const isBlinking = p.invincibleTimer > 0 && Math.floor(p.invincibleTimer / 4) % 2 === 0;
      if (!isBlinking) {
        ctx.font = '32px sans-serif';
        ctx.textAlign = 'center';

        // Flip emoji if facing left
        if (p.facing === 'left') {
          ctx.save();
          ctx.translate(p.x, p.y + 24);
          ctx.scale(-1, 1);
          ctx.fillText('🥷', 0, 0);
          ctx.restore();
        } else {
          ctx.fillText('🥷', p.x, p.y + 24);
        }

        // Sword Slash Visual Effect Arc
        if (p.isSlashing) {
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 3;
          ctx.beginPath();
          if (p.facing === 'right') {
            ctx.arc(p.x + 20, p.y + 10, 24, -Math.PI / 3, Math.PI / 3);
          } else {
            ctx.arc(p.x - 20, p.y + 10, 24, (2 * Math.PI) / 3, (4 * Math.PI) / 3);
          }
          ctx.stroke();
        }
      }

      // Draw Floating Damage Texts
      damageTextsRef.current.forEach((d) => {
        ctx.fillStyle = d.color;
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(d.text, d.x, d.y);
      });

      ctx.restore();
      animId = requestAnimationFrame(gameLoop);
    };

    animId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animId);
  }, [inVillage, currentMapIndex, level, swordTier, maxHp, maxMp, addExp, currentMap, spawnMapMonsters]);

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-rose-400 tracking-wide flex items-center space-x-1.5">
            <span>🥷</span>
            <span>HUYỀN THOẠI NINJA 2D</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-bold border border-rose-500/30">
            ACTION RPG
          </span>
        </div>
      </div>

      {/* Ninja Status HUD (HP, MP, EXP, Gold, Shurikens) */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-2 text-xs">
        <div className="flex items-center justify-between font-bold">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-amber-300 font-black">Cấp {level}</span>
            <span className="text-slate-400">• Kiếm cấp {swordTier}</span>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-yellow-400 font-black">🪙 {gold}</span>
            <span className="text-cyan-300 font-black">🎯 {shurikens}</span>
          </div>
        </div>

        {/* HP Bar */}
        <div className="space-y-0.5">
          <div className="flex justify-between text-[11px] text-slate-400">
            <span>Sinh lực HP:</span>
            <span>{uiHp}/{maxHp}</span>
          </div>
          <div className="w-full h-2 bg-slate-950 rounded-full overflow-hidden border border-slate-800">
            <div
              className="h-full bg-rose-500 transition-all duration-150"
              style={{ width: `${Math.min(100, (uiHp / maxHp) * 100)}%` }}
            />
          </div>
        </div>

        {/* MP & EXP Bars */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-0.5">
            <div className="flex justify-between text-[11px] text-slate-400">
              <span>Chakra MP:</span>
              <span>{uiMp}/{maxMp}</span>
            </div>
            <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-150"
                style={{ width: `${Math.min(100, (uiMp / maxMp) * 100)}%` }}
              />
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex justify-between text-[11px] text-slate-400">
              <span>EXP:</span>
              <span>{exp}/{maxExp}</span>
            </div>
            <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 transition-all duration-150"
                style={{ width: `${Math.min(100, (exp / maxExp) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Map Switcher & Village Rest Toggle */}
      <div className="bg-slate-900/80 p-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs">
        <div className="flex items-center space-x-1 overflow-x-auto">
          {MAPS.map((m, idx) => (
            <button
              key={`map_${m.id}`}
              onClick={() => {
                setInVillage(false);
                setCurrentMapIndex(idx);
              }}
              className={`px-2.5 py-1 rounded-lg font-bold whitespace-nowrap transition-all ${
                !inVillage && currentMapIndex === idx
                  ? 'bg-rose-500 text-white shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>

        <button
          onClick={() => setInVillage(!inVillage)}
          className={`px-3 py-1 rounded-lg font-bold ml-1 transition-all ${
            inVillage
              ? 'bg-emerald-500 text-slate-950 shadow'
              : 'bg-slate-800 text-emerald-400 hover:text-emerald-300'
          }`}
        >
          {inVillage ? '⚔️ Ra Chiến Trường' : '🏡 Vào Làng'}
        </button>
      </div>

      {/* 2D CANVAS BATTLE VIEW OR VILLAGE REST SCREEN */}
      {!inVillage ? (
        <div className="space-y-2">
          <div className="w-full bg-slate-950 rounded-2xl overflow-hidden border-2 border-slate-800 shadow-2xl relative">
            <canvas
              ref={canvasRef}
              width={380}
              height={290}
              className="w-full h-[290px] block"
            />
          </div>

          {/* TOUCH CONTROLS (D-PAD & ACTION BUTTONS) */}
          <div className="bg-slate-900/90 p-3 rounded-2xl border border-slate-800 grid grid-cols-2 gap-4">
            {/* Left D-pad (Move Left / Right) */}
            <div className="flex items-center justify-center space-x-3">
              <button
                onMouseDown={() => (keysRef.current.left = true)}
                onMouseUp={() => (keysRef.current.left = false)}
                onTouchStart={() => (keysRef.current.left = true)}
                onTouchEnd={() => (keysRef.current.left = false)}
                className="w-14 h-14 rounded-2xl bg-slate-800 active:bg-rose-600 border border-slate-700 flex items-center justify-center text-xl font-black text-slate-200 active:scale-95 transition-all shadow-md"
              >
                ◀
              </button>
              <button
                onMouseDown={() => (keysRef.current.right = true)}
                onMouseUp={() => (keysRef.current.right = false)}
                onTouchStart={() => (keysRef.current.right = true)}
                onTouchEnd={() => (keysRef.current.right = false)}
                className="w-14 h-14 rounded-2xl bg-slate-800 active:bg-rose-600 border border-slate-700 flex items-center justify-center text-xl font-black text-slate-200 active:scale-95 transition-all shadow-md"
              >
                ▶
              </button>
            </div>

            {/* Right Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onMouseDown={() => (keysRef.current.jump = true)}
                onMouseUp={() => (keysRef.current.jump = false)}
                onTouchStart={() => (keysRef.current.jump = true)}
                onTouchEnd={() => (keysRef.current.jump = false)}
                className="py-2 px-3 rounded-xl bg-slate-800 active:bg-blue-600 border border-slate-700 font-bold text-xs text-blue-300 flex items-center justify-center space-x-1 active:scale-95"
              >
                <span>🦘</span>
                <span>Nhảy</span>
              </button>

              <button
                onClick={handleMeleeAttack}
                className="py-2 px-3 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 active:scale-95 text-white font-bold text-xs flex items-center justify-center space-x-1 shadow-md"
              >
                <span>🗡️</span>
                <span>Chém</span>
              </button>

              <button
                onClick={handleThrowShuriken}
                className="py-2 px-3 rounded-xl bg-slate-800 active:bg-cyan-600 border border-slate-700 font-bold text-xs text-cyan-300 flex items-center justify-center space-x-1 active:scale-95"
              >
                <span>🎯</span>
                <span>Tiêu</span>
              </button>

              <button
                onClick={handleFireSkill}
                disabled={skillCooldown > 0}
                className="py-2 px-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 disabled:opacity-40 text-white font-bold text-xs flex items-center justify-center space-x-1 active:scale-95 shadow-md"
              >
                <span>🔥</span>
                <span>{skillCooldown > 0 ? `${skillCooldown}s` : 'Hỏa Độn'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* VILLAGE SCREEN (UPGRADES, POTIONS, REST) */
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-4 shadow-xl">
          <div className="flex items-center space-x-3 pb-2 border-b border-slate-800">
            <span className="text-3xl">🏡</span>
            <div>
              <h3 className="font-bold text-sm text-slate-100">Làng Ninja Phong Ma</h3>
              <p className="text-xs text-slate-400">Trạm nghỉ ngơi, phục hồi thể lực và rèn luyện vũ khí.</p>
            </div>
          </div>

          <div className="space-y-2.5">
            {/* Rest at Inn */}
            <div className="p-3 bg-slate-800/80 rounded-xl flex items-center justify-between border border-slate-700/60">
              <div>
                <h4 className="font-bold text-xs text-emerald-400">Trạm Y Tế Làng</h4>
                <p className="text-[11px] text-slate-400">Hồi phục hoàn toàn 100% HP và MP miễn phí</p>
              </div>
              <button
                onClick={restAtInn}
                className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs active:scale-95"
              >
                Nghỉ Ngơi
              </button>
            </div>

            {/* Buy Shurikens */}
            <div className="p-3 bg-slate-800/80 rounded-xl flex items-center justify-between border border-slate-700/60">
              <div>
                <h4 className="font-bold text-xs text-cyan-400">Tiệm Phi Tiêu (x20 cái)</h4>
                <p className="text-[11px] text-slate-400">Giá: 20 Vàng</p>
              </div>
              <button
                onClick={buyShurikens}
                disabled={gold < 20}
                className="px-3.5 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-xs active:scale-95"
              >
                Mua Ngay
              </button>
            </div>

            {/* Upgrade Sword */}
            <div className="p-3 bg-slate-800/80 rounded-xl flex items-center justify-between border border-slate-700/60">
              <div>
                <h4 className="font-bold text-xs text-amber-400">Rèn Kiếm Phong Ma (Cấp {swordTier + 1})</h4>
                <p className="text-[11px] text-slate-400">Tăng mạnh sát thương chém & kỹ năng (Giá: {swordTier * 80} Vàng)</p>
              </div>
              <button
                onClick={upgradeSword}
                disabled={gold < swordTier * 80}
                className="px-3.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-black text-xs active:scale-95"
              >
                Nâng Cấp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
